// Business Intelligence Service for Real-Time Analytics
// Tracks inventory impact, pricing changes, and peak shopping patterns

interface InventoryImpact {
  productId: string;
  productName: string;
  wasInStock: boolean;
  isInStock: boolean;
  stockChange: number;
  timestamp: number;
  sessionImpact: {
    usersViewingDuringStockout: number;
    cartAbandonsFromStockout: number;
    alternativeProductViews: string[];
  };
}

interface PricingChange {
  productId: string;
  productName: string;
  oldPrice: number;
  newPrice: number;
  priceChangePercent: number;
  changeType: 'increase' | 'decrease';
  timestamp: number;
  userReaction: {
    viewsAfterChange: number;
    cartAddsAfterChange: number;
    cartAbandonsAfterChange: number;
  };
}

interface ShoppingPattern {
  hour: number;
  dayOfWeek: number;
  revenueGenerated: number;
  sessionsStarted: number;
  conversionRate: number;
  averageOrderValue: number;
  topCategories: string[];
}

class BusinessIntelligenceService {
  private static instance: BusinessIntelligenceService;
  private inventoryStates: Map<string, { stock: number; inStock: boolean; lastCheck: number }> = new Map();
  private priceStates: Map<string, { price: number; lastCheck: number }> = new Map();
  private shoppingPatterns: Map<string, ShoppingPattern> = new Map(); // Key: hour-dayOfWeek
  private userSessionData: {
    sessionId: string;
    startTime: number;
    viewedProducts: Set<string>;
    cartActions: number;
    purchaseValue?: number;
  } | null = null;

  private constructor() {
    this.initializeTracking();
  }

  static getInstance(): BusinessIntelligenceService {
    if (!BusinessIntelligenceService.instance) {
      BusinessIntelligenceService.instance = new BusinessIntelligenceService();
    }
    return BusinessIntelligenceService.instance;
  }

  private initializeTracking() {
    // Initialize session tracking
    this.startSession();
    
    // Set up periodic inventory checks (every 5 minutes)
    setInterval(() => {
      this.performInventoryCheck();
    }, 5 * 60 * 1000);

    // Track page visibility changes for session handling
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseSession();
      } else {
        this.resumeSession();
      }
    });
  }

  // Session Management
  startSession() {
    const sessionId = sessionStorage.getItem('ss_session_id') || `bi_session_${Date.now()}`;
    this.userSessionData = {
      sessionId,
      startTime: Date.now(),
      viewedProducts: new Set(),
      cartActions: 0
    };
    
    sessionStorage.setItem('ss_session_id', sessionId);
    this.updateShoppingPattern('session_start');
  }

  pauseSession() {
    // Track session pause for accurate time tracking
    if (this.userSessionData) {
      sessionStorage.setItem('ss_session_pause', Date.now().toString());
    }
  }

  resumeSession() {
    // Resume session tracking after pause
    const pauseTime = sessionStorage.getItem('ss_session_pause');
    if (pauseTime && this.userSessionData) {
      const pausedDuration = Date.now() - parseInt(pauseTime);
      this.userSessionData.startTime += pausedDuration; // Adjust start time
      sessionStorage.removeItem('ss_session_pause');
    }
  }

  // Inventory Impact Tracking
  trackProductInventory(productId: string, productName: string, currentStock: number, isInStock: boolean) {
    const previousState = this.inventoryStates.get(productId);
    const now = Date.now();

    if (previousState && previousState.inStock !== isInStock) {
      // Stock status changed - track the impact
      const impact: InventoryImpact = {
        productId,
        productName,
        wasInStock: previousState.inStock,
        isInStock,
        stockChange: currentStock - previousState.stock,
        timestamp: now,
        sessionImpact: {
          usersViewingDuringStockout: 0, // Will be updated by product view events
          cartAbandonsFromStockout: 0,   // Will be updated by cart events
          alternativeProductViews: []     // Will be populated by subsequent views
        }
      };

      // Store for analytics tracking
      this.storeInventoryImpact(impact);
      
      // Track in Firebase Analytics
      import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
        analyticsService.trackEvent('inventory_status_change', {
          product_id: productId,
          product_name: productName,
          previous_status: previousState.inStock ? 'in_stock' : 'out_of_stock',
          new_status: isInStock ? 'in_stock' : 'out_of_stock',
          stock_change: impact.stockChange,
          impact_severity: !isInStock && previousState.inStock ? 'high' : 'medium'
        });
      });
    }

    // Update inventory state
    this.inventoryStates.set(productId, {
      stock: currentStock,
      inStock: isInStock,
      lastCheck: now
    });
  }

  // Price Change Impact Tracking
  trackPriceChange(productId: string, productName: string, newPrice: number) {
    const previousPriceState = this.priceStates.get(productId);
    const now = Date.now();

    if (previousPriceState && previousPriceState.price !== newPrice) {
      const priceChangePercent = ((newPrice - previousPriceState.price) / previousPriceState.price) * 100;
      const changeType = newPrice > previousPriceState.price ? 'increase' : 'decrease';

      const priceChange: PricingChange = {
        productId,
        productName,
        oldPrice: previousPriceState.price,
        newPrice,
        priceChangePercent,
        changeType,
        timestamp: now,
        userReaction: {
          viewsAfterChange: 0,
          cartAddsAfterChange: 0,
          cartAbandonsAfterChange: 0
        }
      };

      // Store for analytics tracking
      this.storePriceChange(priceChange);

      // Track in Firebase Analytics
      import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
        analyticsService.trackEvent('price_change_detected', {
          product_id: productId,
          product_name: productName,
          old_price: previousPriceState.price,
          new_price: newPrice,
          price_change_percent: priceChangePercent,
          change_type: changeType,
          change_magnitude: Math.abs(priceChangePercent) > 20 ? 'significant' : 'moderate'
        });
      });
    }

    // Update price state
    this.priceStates.set(productId, {
      price: newPrice,
      lastCheck: now
    });
  }

  // User Behavior During Stockouts
  trackProductViewDuringStockout(productId: string, isInStock: boolean) {
    if (!isInStock) {
      // User viewed an out-of-stock product
      import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
        analyticsService.trackEvent('out_of_stock_product_view', {
          product_id: productId,
          user_intent: 'browse_unavailable',
          potential_lost_sale: true
        });
      });
    }

    // Track in session data
    if (this.userSessionData) {
      this.userSessionData.viewedProducts.add(productId);
    }
  }

  // Cart Abandonment Due to Stockout
  trackStockoutCartImpact(productId: string, cartValue: number) {
    import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
      analyticsService.trackEvent('stockout_cart_impact', {
        product_id: productId,
        potential_lost_revenue: cartValue,
        abandonment_cause: 'inventory_unavailable'
      });
    });
  }

  // Peak Shopping Time Analysis
  updateShoppingPattern(eventType: 'session_start' | 'purchase' | 'cart_action', value: number = 0) {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.getDay(); // 0 = Sunday
    const patternKey = `${hour}-${dayOfWeek}`;

    let pattern = this.shoppingPatterns.get(patternKey);
    if (!pattern) {
      pattern = {
        hour,
        dayOfWeek,
        revenueGenerated: 0,
        sessionsStarted: 0,
        conversionRate: 0,
        averageOrderValue: 0,
        topCategories: []
      };
    }

    switch (eventType) {
      case 'session_start':
        pattern.sessionsStarted += 1;
        break;
      case 'purchase':
        pattern.revenueGenerated += value;
        this.userSessionData!.purchaseValue = value;
        break;
      case 'cart_action':
        if (this.userSessionData) {
          this.userSessionData.cartActions += 1;
        }
        break;
    }

    this.shoppingPatterns.set(patternKey, pattern);

    // Track peak times in Firebase Analytics
    if (eventType === 'purchase') {
      import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
        analyticsService.trackEvent('peak_time_purchase', {
          hour_of_day: hour,
          day_of_week: dayOfWeek,
          day_name: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
          purchase_value: value,
          is_peak_hour: this.isPeakShoppingHour(hour, dayOfWeek),
          shopping_pattern_key: patternKey
        });
      });
    }
  }

  // Determine if current time is a peak shopping hour
  isPeakShoppingHour(hour: number, dayOfWeek: number): boolean {
    const pattern = this.shoppingPatterns.get(`${hour}-${dayOfWeek}`);
    if (!pattern) return false;

    // Calculate average sessions for this day of week
    const allPatterns = Array.from(this.shoppingPatterns.values())
      .filter(p => p.dayOfWeek === dayOfWeek);
    
    if (allPatterns.length === 0) return false;

    const averageSessions = allPatterns.reduce((sum, p) => sum + p.sessionsStarted, 0) / allPatterns.length;
    return pattern.sessionsStarted > averageSessions * 1.5; // 50% above average = peak
  }

  // Real-time Business Metrics
  getCurrentBusinessMetrics() {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDayOfWeek = now.getDay();
    const currentPatternKey = `${currentHour}-${currentDayOfWeek}`;
    const currentPattern = this.shoppingPatterns.get(currentPatternKey);

    // Get real-time inventory alerts
    const lowStockItems = Array.from(this.inventoryStates.entries())
      .filter(([_, state]) => state.stock <= 5 && state.inStock)
      .map(([productId, state]) => ({ productId, stock: state.stock }));

    // Get recent price changes (last 24 hours)
    const recentPriceChanges = Array.from(this.priceStates.entries())
      .filter(([_, state]) => Date.now() - state.lastCheck < 24 * 60 * 60 * 1000);

    return {
      current_time_metrics: {
        hour: currentHour,
        day_of_week: currentDayOfWeek,
        is_peak_time: this.isPeakShoppingHour(currentHour, currentDayOfWeek),
        current_pattern: currentPattern || null
      },
      inventory_alerts: {
        low_stock_count: lowStockItems.length,
        low_stock_items: lowStockItems
      },
      pricing_intelligence: {
        recent_price_changes: recentPriceChanges.length,
        price_monitoring_active: true
      },
      session_insights: this.userSessionData ? {
        session_duration: Date.now() - this.userSessionData.startTime,
        products_viewed: this.userSessionData.viewedProducts.size,
        cart_actions: this.userSessionData.cartActions,
        has_purchase: !!this.userSessionData.purchaseValue
      } : null
    };
  }

  // Periodic inventory check (called every 5 minutes)
  private async performInventoryCheck() {
    // This would typically call an API to get current inventory levels
    import('../services/AnalyticsService.ts').then(({ analyticsService }) => {
      analyticsService.trackEvent('inventory_check_performed', {
        products_monitored: this.inventoryStates.size,
        timestamp: Date.now(),
        check_type: 'scheduled'
      });
    });
  }

  // Storage helpers for offline analytics
  private storeInventoryImpact(impact: InventoryImpact) {
    try {
      const stored = JSON.parse(localStorage.getItem('ss_inventory_impacts') || '[]');
      stored.push(impact);
      // Keep only last 100 impacts
      const recent = stored.slice(-100);
      localStorage.setItem('ss_inventory_impacts', JSON.stringify(recent));
    } catch (error) {
      // Failed to store inventory impact silently
    }
  }

  private storePriceChange(change: PricingChange) {
    try {
      const stored = JSON.parse(localStorage.getItem('ss_price_changes') || '[]');
      stored.push(change);
      // Keep only last 50 price changes
      const recent = stored.slice(-50);
      localStorage.setItem('ss_price_changes', JSON.stringify(recent));
    } catch (error) {
      // Failed to store price change silently
    }
  }
}

export default BusinessIntelligenceService;